import React from "react";
import { RadioInput } from "./RadioInput";
import { Fieldset } from "../Fieldset";
import { Label } from "../Label";
import { Error } from "../Error";
import { concatStringFromClassesArrayList } from "../../Utils";
import { sanitiseString } from "../../Utils";

export interface Props {
  id?: string;
  classes?: string;
  legend?: string;
  legendClasses?: string;
  description?: string;
  borderless?: boolean;
  name: string;
  radios: RadioOptions;
  dontWrap?: boolean;
  legendIsQuestionTitle?: boolean;
  error?: React.ComponentProps<typeof Error>;
  dataTestId: string;
}

export interface RadioOptions {
  radioList: {
    id: string;
    value?: string;
    classes?: string;
    checked?: boolean;
    disabled?: boolean;
    label: React.ComponentProps<typeof Label>;
    onClick?: React.MouseEventHandler<HTMLInputElement>;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    attributes?: { [key: string]: string };
  }[];
}

export const Radios = (props: Props) => {
  const sanitisedId = sanitiseString(props.id);
  const sanitisedDataTestId = sanitiseString(props.dataTestId);

  const fieldClassList = {
    defaultClass: "",
    optionalClasses: [props.classes || null],
  };
  const fieldClassName = concatStringFromClassesArrayList(fieldClassList);

  const itemContainerClassList = {
    defaultClass: "ons-radios__item",
    optionalClasses: [props.borderless ? "ons-radios__item--no-border" : ""],
  };
  const itemContainerClassName = concatStringFromClassesArrayList(
    itemContainerClassList
  );

  const itemClassList = {
    optionalClasses: [props.borderless ? "ons-radio--no-border" : ""],
  };
  const itemClassName = concatStringFromClassesArrayList(itemClassList);

  const radios = (
    <div className="ons-radios__items">
      {props.radios.radioList.map((item, index) => {
        const sanitisedItemId = sanitiseString(item.id);
        const baseItemDataTestId = `${sanitisedDataTestId}-item-${sanitisedItemId}`;

        return (
          <React.Fragment key={sanitisedItemId}>
            <span
              className={itemContainerClassName}
              data-testid={`${baseItemDataTestId}-container`}
              key={sanitisedItemId}
            >
              <RadioInput
                id={sanitisedItemId}
                label={{
                  text: item.label.text,
                  description: item.label.description,
                  iconType: item.label.iconType,
                }}
                value={item.value}
                classes={itemClassName}
                dataTestId={baseItemDataTestId}
                name={props.name}
                onClick={item.onClick}
                onChange={item.onChange}
                checked={item.checked}
                disabled={item.disabled}
              />
            </span>
            {index !== props.radios.radioList.length - 1 && <br />}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <Fieldset
      id={sanitisedId}
      classes={fieldClassName}
      legend={props.legend}
      legendClasses={props.legendClasses}
      description={props.description}
      dontWrap={props.dontWrap}
      legendIsQuestionTitle={props.legendIsQuestionTitle}
      error={props.error}
      dataTestId={`fieldset-${sanitisedDataTestId}`}
    >
      {radios}
    </Fieldset>
  );
};
